import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useApp } from '../context/AppContext';

const emptyForm = { name: '', emp_code: '', department_id: '', role: 'employee', is_active: true };

export default function Employees() {
  const { toast } = useApp();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');

  const load = () => {
    Promise.all([api.getEmployees(), api.getDepartments()]).then(([emps, depts]) => {
      setEmployees(emps);
      setDepartments(depts);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setForm({ ...emptyForm, department_id: departments[0]?.id || '' });
    setEditId(null);
    setModal('form');
  };

  const openEdit = (emp) => {
    setForm({ name: emp.name, emp_code: emp.emp_code || '', department_id: emp.department_id, role: emp.role, is_active: !!emp.is_active });
    setEditId(emp.id);
    setModal('form');
  };

  const handleSave = async () => {
    if (!form.name || !form.department_id) return toast('Name and department are required', 'error');
    try {
      if (editId) {
        await api.updateEmployee(editId, form);
        toast('Employee updated');
      } else {
        await api.createEmployee(form);
        toast('Employee added');
      }
      setModal(null);
      load();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const handleDeactivate = async (emp) => {
    await api.deleteEmployee(emp.id);
    toast(`${emp.name} deactivated`);
    load();
  };

  const filtered = employees.filter(e => {
    const matchSearch = e.name.toLowerCase().includes(search.toLowerCase()) ||
      (e.emp_code || '').toLowerCase().includes(search.toLowerCase());
    const matchDept = !deptFilter || e.department_id === parseInt(deptFilter);
    return matchSearch && matchDept;
  });

  return (
    <div className="page-body">
      <div className="search-bar">
        <div className="search-input-wrap" style={{ flex: 1 }}>
          <span className="search-icon">🔍</span>
          <input placeholder="Search by name or code..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select
          value={deptFilter}
          onChange={e => setDeptFilter(e.target.value)}
          style={{ width: 180 }}
        >
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Employee</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Emp Code</th>
                <th>Department</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6}><div className="loading-center"><span className="spinner" /></div></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6}><div className="empty-state"><p>No employees found</p></div></td></tr>
              ) : filtered.map(emp => (
                <tr key={emp.id}>
                  <td style={{ color: 'var(--iocl-white)', fontWeight: 500 }}>{emp.name}</td>
                  <td style={{ color: 'var(--iocl-muted)', fontFamily: 'monospace' }}>{emp.emp_code || '—'}</td>
                  <td>
                    <span className="badge badge-blue">{emp.department_name}</span>
                  </td>
                  <td style={{ color: 'var(--iocl-light)', textTransform: 'capitalize' }}>{emp.role}</td>
                  <td>
                    <span className={`badge ${emp.is_active ? 'badge-green' : 'badge-red'}`}>
                      {emp.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(emp)}>Edit</button>
                      {emp.is_active && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeactivate(emp)}>Deactivate</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal === 'form' && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">{editId ? 'Edit Employee' : 'Add Employee'}</h3>
              <button className="btn btn-secondary btn-sm btn-icon" onClick={() => setModal(null)}>✕</button>
            </div>

            <div className="form-group">
              <label>Full Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Employee name" />
            </div>
            <div className="two-col">
              <div className="form-group">
                <label>Employee Code</label>
                <input value={form.emp_code} onChange={e => setForm(f => ({ ...f, emp_code: e.target.value }))} placeholder="EMP001" />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="employee">Employee</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Department *</label>
              <select value={form.department_id} onChange={e => setForm(f => ({ ...f, department_id: parseInt(e.target.value) }))}>
                <option value="">-- Select Department --</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name} ({d.code})</option>)}
              </select>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>
                {editId ? 'Save Changes' : 'Add Employee'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
